export interface RenderResult {
  rendered: string;
  missingVars: string[];
}

/**
 * Wrap rendered email-body HTML in a minimal document for use as an iframe
 * `srcdoc`. The wrapper forces a sans-serif font so the preview/viewer renders
 * like the real email rather than the browser default serif (Times). Preview
 * styling only — the actual email body is whatever the template produces.
 * `<base target="_blank">` sends link clicks to a new browser tab instead of
 * navigating the iframe to the target (which blanked the frame); it requires
 * `allow-popups` on the iframe sandbox.
 */
export function wrapHtmlForIframe(content: string): string {
  return (
    '<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank">' +
    '<style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
    'font-size:14px;line-height:1.5;color:#212529;margin:0.75rem;}</style></head><body>' +
    content +
    '</body></html>'
  );
}

/**
 * HTML-encode a variable value for the encoded ({{name}}) substitution path.
 * Matches .NET `System.Net.WebUtility.HtmlEncode` — the encoder the authoritative
 * F# renderer uses at enqueue time (TemplateRenderer.fs) — so the client preview
 * equals the real email body. WebUtility encodes the five named specials below,
 * the Latin-1 supplement range U+00A0–U+00FF (e.g. accented names like
 * José/Núñez → numeric references) and supplementary-plane (astral) characters,
 * but leaves other BMP non-ASCII (e.g. Œ, 中) as-is. The shared renderer-cases.json
 * fixture pins this parity across both renderers.
 */
function htmlEncode(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/[\xA0-\xFF]/g, (c) => `&#${c.charCodeAt(0)};`)
    .replace(/[\u{10000}-\u{10FFFF}]/gu, (c) => `&#${c.codePointAt(0)};`);
}

/**
 * Client-side port of TemplateRenderer.render (F#). Substitutes {{name}}
 * (HTML-encoded) and {{{name}}} (raw) from `data`; missing variables render as
 * {{?name}} and are reported. {{{{ }}}} escape to literal {{ }}. Lookups are
 * case-sensitive. The shared renderer-cases.json fixture keeps this in lockstep
 * with the F# renderer.
 */
export function renderTemplate(template: string, data: Record<string, string>): RenderResult {
  let out = '';
  const missing: string[] = [];
  const seen = new Set<string>();
  const n = template.length;
  let i = 0;

  const emit = (name: string, raw: boolean): void => {
    if (Object.prototype.hasOwnProperty.call(data, name)) {
      const value = data[name];
      out += raw ? value : htmlEncode(value);
    } else {
      out += '{{?' + name + '}}';
      if (!seen.has(name)) {
        seen.add(name);
        missing.push(name);
      }
    }
  };

  while (i < n) {
    const c = template[i];

    // {{{{ -> literal {{   (checked before triple-brace so quadruple wins)
    if (c === '{' && i + 3 < n && template[i + 1] === '{' && template[i + 2] === '{' && template[i + 3] === '{') {
      out += '{{';
      i += 4;
    }
    // }}}} -> literal }}
    else if (c === '}' && i + 3 < n && template[i + 1] === '}' && template[i + 2] === '}' && template[i + 3] === '}') {
      out += '}}';
      i += 4;
    }
    // {{{name}}} -> substitute raw (no HTML encoding)
    else if (c === '{' && i + 2 < n && template[i + 1] === '{' && template[i + 2] === '{') {
      let j = i + 3;
      let closeAt = -1;
      while (j + 2 < n && closeAt < 0) {
        if (template[j] === '}' && template[j + 1] === '}' && template[j + 2] === '}') {
          closeAt = j;
        } else {
          j += 1;
        }
      }
      if (closeAt >= 0) {
        emit(template.slice(i + 3, closeAt), true);
        i = closeAt + 3;
      } else {
        out += c;
        i += 1;
      }
    }
    // {{name}} -> substitute (HTML-encoded)
    else if (c === '{' && i + 1 < n && template[i + 1] === '{') {
      let j = i + 2;
      let closeAt = -1;
      while (j + 1 < n && closeAt < 0) {
        if (template[j] === '}' && template[j + 1] === '}') {
          closeAt = j;
        } else {
          j += 1;
        }
      }
      if (closeAt >= 0) {
        emit(template.slice(i + 2, closeAt), false);
        i = closeAt + 2;
      } else {
        out += c;
        i += 1;
      }
    } else {
      out += c;
      i += 1;
    }
  }

  return { rendered: out, missingVars: missing };
}
